import { Tooltip } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import classNames from "classnames";
import "./submittal-micro-viz.scss";
import { DATE_FORMAT_MMDDYYYY, DateUtils } from "utils/dateutils";

type TSubmittalMicroVisualiserProps = {
  size?: "sm" | "md" | "lg";
  gc_reviewer_id: string;
  design_reviewer_id: string;
  submittal_history: any;
  vendorInfo?: any;
  disabledTooltip?: boolean;
  titleBar?: boolean;
};

const sizes = {
  sm: 16,
  md: 24,
  lg: 32
};

const getPendingStages = (lastStage: string) => {
  const stages = ["NA", "SC", "GC", "D", "DO"];

  const startFrom = stages.indexOf(lastStage);

  const pendingStages: any = [];
  for (let s = startFrom + 1; s < stages.length; s++) {
    const alphabet = stages[s].includes("-")
      ? // unreachable: stages is the literal ["NA","SC","GC","D","DO"], none contain "-", so includes("-") is always false
        /* v8 ignore next */
        stages[s].split("-")[1]
      : stages[s];
    pendingStages.push({
      statusAlphabet: alphabet,
      stageInProgress: !!(lastStage === "NA" && alphabet === "NA"),
      stageCompleted: false,
      hId: stages[s]
    });
  }
  return pendingStages;
};

type TGenerateVizBlocksOptions = {
  gcReviewerId: string;
  designReviewerId: string;
  vendorInfo?: any;
};

const checkForRejection = (
  historyItem: any,
  idx: number,
  allHistoryItems: any
) => {
  // unreachable: the only caller buildScBlock runs guarded by historyItem.status === 2, so status !== 2 is always false here
  /* v8 ignore next 6 */
  if (historyItem.status !== 2) {
    return {
      rejected: false,
      rejectorIndex: null
    };
  }

  if (allHistoryItems[idx + 2]?.status === 2) {
    return {
      rejected: true,
      rejectorIndex: idx + 1
    };
  }

  for (let h = idx + 1; h < allHistoryItems.length; h++) {
    if (allHistoryItems[h].status === 2) {
      return {
        rejected: true,
        rejectorIndex: h - 1
      };
    }
  }

  return {
    rejected: false,
    rejectorIndex: null
  };
};

const getAssigneeUserType = (
  assignee: string,
  gcReviewerId: string,
  designReviewerId: string
) => {
  if (assignee === gcReviewerId) {
    return "GC";
  }
  if (assignee && assignee === designReviewerId) {
    return "DR";
  }

  return null;
};

const getUserInfoString = (
  historyItem: any,
  userType: "GC" | "SC" | "DR",
  vendorInfo?: any
) => {
  try {
    if (historyItem.assignee_user) {
      const fullName = `${historyItem?.assignee_user?.first_name} ${historyItem?.assignee_user?.last_name}`;
      let companyName = "";
      if (historyItem?.assignee_user?.project_users) {
        companyName = historyItem?.assignee_user?.project_users[0]
          .subscription_vendor
          ? historyItem?.assignee_user?.project_users[0].subscription_vendor
              .name
          : historyItem.assignee_user.subscription_users[0].subscription.name;
      }
      return companyName ? `${fullName}, ${companyName}` : fullName;
    }

    let pocCompanyName = "";
    if (vendorInfo && historyItem.assignee_unregistered) {
      if (userType === "SC") {
        pocCompanyName = `, ${vendorInfo?.responsibleContractorCompanyName}`;
      }
      if (userType === "DR") {
        pocCompanyName = `, ${vendorInfo?.designReviewerCompanyName}`;
      }
      return `${historyItem.assignee_unregistered}${pocCompanyName}`;
    }
  } catch (ex: any) {
    console.error("getUserInfoString: ", ex.message);
  }
  return "-";
};

const getReviewResponseText = (submittalResponseId: number) =>
  submittalResponseId > 4 ? "Rejected" : "Approved";

const buildScTooltipData = (
  historyItem: any,
  index: number,
  historyItems: any[],
  stageInProgress: boolean,
  options: TGenerateVizBlocksOptions
) => {
  const tooltipData: any = {};

  if (stageInProgress) {
    tooltipData.actionInfo = `Assigned to ${getUserInfoString(
      historyItem,
      "SC",
      options.vendorInfo
    )}`;
    tooltipData.comment =
      historyItems[index - 1].status === 1 ? historyItem.comment : null;
    return tooltipData;
  }

  tooltipData.actionInfo = `Submitted by ${getUserInfoString(
    historyItem,
    "SC",
    options.vendorInfo
  )}`;
  tooltipData.comment = historyItems[index + 1].comment;
  return tooltipData;
};

const buildSingleSubmitBlock = (historyItem: any, historyItems: any[]) => ({
  status: historyItem.status,
  statusAlphabet: "SC",
  hId: historyItem.id,
  stageInProgress: false,
  stageCompleted: historyItems.length > 1
});

const buildScBlock = (
  historyItem: any,
  index: number,
  historyItems: any[],
  options: TGenerateVizBlocksOptions
) => {
  const rejection = checkForRejection(historyItem, index, historyItems);
  const stageInProgress = !historyItems[index + 1];

  return {
    status: historyItem.status,
    statusAlphabet: "SC",
    hId: historyItem.id,
    hasBeenRejected: rejection.rejected,
    rejectorIndex: rejection.rejectorIndex,
    cancelled: rejection.rejected,
    stageInProgress,
    stageCompleted: !stageInProgress,
    originalHistoryItem: historyItem,
    tooltipData: buildScTooltipData(
      historyItem,
      index,
      historyItems,
      stageInProgress,
      options
    )
  };
};

const isGcInitialRejection = (historyItems: any[], index: number) =>
  historyItems[index + 1]?.status === 2;

const hasGcBeenRejected = (
  historyItems: any[],
  index: number,
  options: TGenerateVizBlocksOptions
) => {
  const nextItem = historyItems[index + 1];
  const nextNextItem = historyItems[index + 2];

  return (
    (nextItem?.status === 3 &&
      nextNextItem?.status === 3 &&
      getAssigneeUserType(
        nextNextItem.assignee,
        options.gcReviewerId,
        options.designReviewerId
      ) === "GC" &&
      nextNextItem.submittal_response_id > 4) ||
    (nextItem?.status === 3 && nextItem.submittal_response_id > 4)
  );
};

const isGcFinalStage = (
  historyItems: any[],
  index: number,
  options: TGenerateVizBlocksOptions
) => {
  const historyItem = historyItems[index];
  const previousItem = historyItems[index - 1];
  const nextItem = historyItems[index + 1];

  return (
    (historyItem.status >= 3 &&
      historyItem.submittal_response_id <= 4 &&
      previousItem &&
      getAssigneeUserType(
        previousItem.assignee || previousItem.assignee_unregistered,
        options.gcReviewerId,
        options.designReviewerId
      ) === "DR") ||
    nextItem?.status === 4
  );
};

const buildGcTooltipData = (
  historyItem: any,
  index: number,
  historyItems: any[],
  stageInProgress: boolean,
  stageCompleted: boolean,
  isFinalStage: boolean,
  options: TGenerateVizBlocksOptions
) => {
  const tooltipData: any = {};

  if (stageInProgress) {
    tooltipData.actionInfo = `In Review with ${getUserInfoString(
      historyItem,
      "GC",
      options.vendorInfo
    )}`;
    tooltipData.comment = null;
    return tooltipData;
  }

  // unreachable: buildGcBlock sets stageCompleted = !stageInProgress; reaching here means stageInProgress was false, so stageCompleted is always true
  /* v8 ignore next 3 */
  if (!stageCompleted) {
    return tooltipData;
  }

  if (isFinalStage) {
    tooltipData.actionInfo = "Closed & Distributed";
    tooltipData.comment = historyItems[index + 1].comment;
    return tooltipData;
  }

  const nextHistoryItem = historyItems[index + 1];
  const responseText = getReviewResponseText(
    nextHistoryItem.submittal_response_id
  );
  tooltipData.actionInfo = `${responseText} by ${getUserInfoString(
    historyItem,
    "GC",
    options.vendorInfo
  )}`;
  tooltipData.comment = historyItems[index + 1].comment;
  return tooltipData;
};

const buildGcBlock = (
  historyItem: any,
  index: number,
  historyItems: any[],
  options: TGenerateVizBlocksOptions
) => {
  const hasRejected = isGcInitialRejection(historyItems, index);
  const hasBeenRejected = hasGcBeenRejected(historyItems, index, options);
  const isFinalStage = isGcFinalStage(historyItems, index, options);
  const stageInProgress = !historyItems[index + 1];
  const stageCompleted = !!historyItems[index + 1];

  return {
    status: historyItem.status,
    reviewer: "GC-R",
    statusAlphabet: isFinalStage ? "DO" : "GC",
    hId: historyItem.id,
    hasRejected,
    hasBeenRejected,
    cancelled: hasBeenRejected,
    stageInProgress,
    stageCompleted,
    originalHistoryItem: historyItem,
    tooltipData: buildGcTooltipData(
      historyItem,
      index,
      historyItems,
      stageInProgress,
      stageCompleted,
      isFinalStage,
      options
    )
  };
};

const buildDrTooltipData = (
  historyItem: any,
  index: number,
  historyItems: any[],
  stageInProgress: boolean,
  stageCompleted: boolean,
  options: TGenerateVizBlocksOptions
) => {
  const tooltipData: any = {};

  if (stageInProgress) {
    tooltipData.actionInfo = `Assigned to ${getUserInfoString(
      historyItem,
      "DR",
      options.vendorInfo
    )}`;
    tooltipData.comment = null;
    return tooltipData;
  }

  // unreachable: buildDrBlock sets stageCompleted = !stageInProgress; reaching here means stageInProgress was false, so stageCompleted is always true
  /* v8 ignore next 3 */
  if (!stageCompleted) {
    return tooltipData;
  }

  const nextHistoryItem = historyItems[index + 1];
  const responseText = getReviewResponseText(
    nextHistoryItem.submittal_response_id
  );
  tooltipData.actionInfo = `${responseText} by ${getUserInfoString(
    historyItem,
    "DR",
    options.vendorInfo
  )}`;
  tooltipData.comment = historyItems[index + 1].comment;
  return tooltipData;
};

const buildDrBlock = (
  historyItem: any,
  index: number,
  historyItems: any[],
  options: TGenerateVizBlocksOptions
) => {
  const hasRejected =
    historyItems[index + 1]?.status === 3 &&
    historyItems[index + 1].submittal_response_id > 4;
  const stageInProgress = !historyItems[index + 1];
  const stageCompleted = !!historyItems[index + 1];

  return {
    status: historyItem.status,
    reviewer: "DR-R",
    statusAlphabet: "D",
    hId: historyItem.id,
    hasRejected,
    cancelled: hasRejected,
    stageInProgress,
    stageCompleted,
    originalHistoryItem: historyItem,
    tooltipData: buildDrTooltipData(
      historyItem,
      index,
      historyItems,
      stageInProgress,
      stageCompleted,
      options
    )
  };
};

const buildBlockForHistoryItem = (
  historyItem: any,
  index: number,
  historyItems: any[],
  options: TGenerateVizBlocksOptions
) => {
  if (historyItems.length === 1 && historyItem.status === 1) {
    return buildSingleSubmitBlock(historyItem, historyItems);
  }

  if (historyItem.status === 2) {
    return buildScBlock(historyItem, index, historyItems, options);
  }

  if (
    historyItem.status === 3 &&
    getAssigneeUserType(
      historyItem.assignee,
      options.gcReviewerId,
      options.designReviewerId
    ) === "GC"
  ) {
    return buildGcBlock(historyItem, index, historyItems, options);
  }

  if (
    historyItem.status === 3 &&
    getAssigneeUserType(
      historyItem.assignee || historyItem.assignee_unregistered,
      options.gcReviewerId,
      options.designReviewerId
    ) === "DR"
  ) {
    return buildDrBlock(historyItem, index, historyItems, options);
  }

  return null;
};

const applyCancellationFlags = (vizBlocks: any[]) => {
  let startCancelling = false;

  vizBlocks.forEach((block, index) => {
    if (block.hasBeenRejected) {
      startCancelling = true;
    }

    block.cancelled = startCancelling;

    if (block.hasRejected) {
      startCancelling = false;

      if (vizBlocks[index + 1]?.hasRejected) {
        startCancelling = true;
      }
    }
  });
};

const appendPendingStages = (vizBlocks: any[]) => {
  // unreachable: no viz block ever carries status 4 (status-4 history items map to null and are filtered out), so this is always false
  /* v8 ignore next 3 */
  if (vizBlocks[vizBlocks.length - 1].status === 4) {
    return vizBlocks;
  }

  const lastStageAlphabet = vizBlocks[vizBlocks.length - 1].statusAlphabet;
  return [...vizBlocks, ...getPendingStages(lastStageAlphabet)];
};

const generateVizBlocksFromHistory = (
  historyItems: any[],
  options: TGenerateVizBlocksOptions
) => {
  if (historyItems.length === 0) {
    return getPendingStages("NA");
  }

  const vizBlocks = historyItems
    .map((historyItem, index) =>
      buildBlockForHistoryItem(historyItem, index, historyItems, options)
    )
    .filter(Boolean);

  applyCancellationFlags(vizBlocks);
  return appendPendingStages(vizBlocks);
};

function TooltipContents({ history, tooltipData }: any) {
  const { actionInfo } = tooltipData;
  const createdAt = DateUtils.format(history.created_at, DATE_FORMAT_MMDDYYYY);

  return (
    <div className="text-xs">
      <div>{createdAt}</div>
      <hr />
      {actionInfo && <div>{actionInfo}</div>}
      {tooltipData.comment && (
        <>
          <hr />
          <div>Comment: {tooltipData.comment}</div>
        </>
      )}
    </div>
  );
}

function VizBlock({ blockDetails, isStart, isEnd }: any) {
  const className = classNames("viz-block", {
    "viz-block--in-progress": blockDetails.stageInProgress,
    "viz-block--completed-rejected":
      blockDetails.stageCompleted && blockDetails.hasRejected,
    "viz-block--completed-not-rejected":
      blockDetails.stageCompleted && !blockDetails.hasRejected,
    "viz-block--first": isStart,
    "viz-block--last": isEnd
  });

  return (
    <div className={className}>
      <span>
        {blockDetails.statusAlphabet === "DO" ? (
          <>&#10003;</>
        ) : (
          blockDetails.statusAlphabet
        )}
      </span>
      {blockDetails.cancelled && <span className="viz-block--cancelled" />}
    </div>
  );
}

export default function SubmittalMicroVisualiser(
  props: TSubmittalMicroVisualiserProps
) {
  const {
    size = "md",
    gc_reviewer_id,
    design_reviewer_id,
    submittal_history,
    vendorInfo,
    disabledTooltip = false,
    titleBar = false
  } = props;

  const [vizStack, setVizStack] = useState<any>([]);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [showCount, setShowCount] = useState<number>(0);
  const [viewIndex, setViewIndex] = useState<number>(0);
  const [showSliderButtons, setShowSliderButtons] = useState<boolean>(false);

  const generateVizBlocks = useCallback(
    (historyItems: any) =>
      generateVizBlocksFromHistory(historyItems, {
        gcReviewerId: gc_reviewer_id,
        designReviewerId: design_reviewer_id,
        vendorInfo
      }),
    [design_reviewer_id, gc_reviewer_id, vendorInfo]
  );

  useEffect(() => {
    const clonedReversedHistoryArr = (submittal_history || [])
      .slice()
      .reverse();

    const filteredHistory = clonedReversedHistoryArr.filter(
      (history: any) => history.status
    );

    const vizBlocks = generateVizBlocks(filteredHistory);
    setVizStack(vizBlocks);
  }, [generateVizBlocks, submittal_history]);

  useEffect(() => {
    let numStatusToShow = Math.floor(
      (containerRef.current?.clientWidth || 0) / sizes[size]
    );
    const numStatus = vizStack.length;
    let showNav = false;
    if (numStatus > numStatusToShow) {
      // account for buttons
      showNav = true;
      numStatusToShow -= 2;
    } else {
      // clip at max number of status
      numStatusToShow = numStatus;
    }
    setShowCount(numStatusToShow);
    setViewIndex(vizStack.length - numStatusToShow);
    setShowSliderButtons(showNav);
  }, [vizStack, size]);

  const showPrevious = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setViewIndex((prev) => prev - 1);
    return false;
  };

  const showNext = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setViewIndex((prev) => prev + 1);
    return false;
  };

  const slicedVizStack = vizStack.slice(viewIndex, viewIndex + showCount);
  const isViewingFirst = viewIndex === 0;
  const isViewingLast = viewIndex + showCount === vizStack.length;

  const sizeClassname = classNames({
    "viz-block--small": size === "sm",
    "viz-block--medium": size === "md",
    "viz-block--large": size === "lg"
  });

  const contentClassname = () => {
    if (titleBar) {
      return "flex-end";
    }

    return showSliderButtons ? "center" : "auto";
  };

  return (
    <div ref={containerRef} className={sizeClassname} data-testid="smv-root">
      <div className="flex">
        {showSliderButtons && (
          <button
            type="button"
            className="viz-block--button"
            disabled={isViewingFirst}
            onClick={showPrevious}
            data-testid="smv-prev-button"
          >
            <IconChevronLeft size={sizes[size]} />
          </button>
        )}
        <div className="flex-grow">
          <div
            className="flex"
            style={{
              justifyContent: contentClassname()
            }}
            data-testid="smv-track"
          >
            {slicedVizStack.map((item: any, index: number) => {
              const content = (
                <div
                  className="viz-block--wrapper"
                  key={index}
                  data-testid={`smv-block-${index}`}
                >
                  <VizBlock
                    blockDetails={item}
                    isStart={viewIndex === 0 && index === 0}
                    isEnd={
                      index === slicedVizStack.length - 1 &&
                      viewIndex + showCount === vizStack.length
                    }
                    size={size}
                  />
                </div>
              );
              return item.originalHistoryItem ? (
                <Tooltip
                  key={index}
                  title={
                    disabledTooltip ? (
                      ""
                    ) : (
                      <TooltipContents
                        history={item.originalHistoryItem}
                        tooltipData={item.tooltipData}
                      />
                    )
                  }
                  data-testid="smv-block-tooltip"
                >
                  {content}
                </Tooltip>
              ) : (
                content
              );
            })}
          </div>
        </div>
        {showSliderButtons && (
          <button
            type="button"
            className="viz-block--button"
            onClick={showNext}
            disabled={isViewingLast}
            data-testid="smv-next-button"
          >
            <IconChevronRight size={sizes[size]} />
          </button>
        )}
      </div>
    </div>
  );
}
